// cSpell:ignore Aangepast Bedrag betaaluitnodigingen forfait maanden meegenomen privatieve provisies Scenariolaan Schatting toegevoegd untick unticked verrekend Zelf
/* eslint-disable no-nested-ternary */
// Provisions step: one card per unit, headed by its full address, each a
// selectable table over that unit's overlapping rental contracts — the same
// grammar and the same cards as the cost-selection step (checkbox = include,
// a subtotal under each card, a grand total under a building's cards).
// Full-width label↔value cards proved unreadable at dashboard width; one card
// over every unit hid which tenant belongs to which property (Lewis,
// 2026-10-16). Inside a card, each tenant heads their own provision lines,
// and every line has its own checkbox (provisionLineInclusion.utils); the
// table is ProvisionLinesTable.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  Card,
  EmptyState,
  Icon,
  Stack,
  TextStyle,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import styled from "styled-components";
import { LocaleNumberField } from "../../../../components/LocaleNumberField";
import { Money } from "../../../../components/Money";
import { IProvisionLine } from "../../../../contractProvision.utils";
import {
  ComputeResult,
  contractProvisionSlices,
  IIncludedLeaseProvision,
  includedLeaseProvision,
  Interval,
  IReopenedLine,
  leaseExpectedProvision,
  leaseFixedFeeKinds,
  monthsBetween,
  provisionIntervalsByContract,
  provisionIntervalsBySlice,
  reopenedProvisionLines,
  unionIntervals,
  unionOfSlices,
} from "../../../../engine";
import { extraProvisionKindOptions } from "../../../../extraProvisionForm.utils";
import { unitAddressLabel, unitBoxLabel } from "../../../../groupUnits.utils";
import { todayIsoDay } from "../../../../localDay.utils";
import { formatMoney } from "../../../../moneyText.utils";
import { MONEY_DECIMALS } from "../../../../numberInput.utils";
import {
  byLeaseStart,
  IProvisionCard,
  provisionCards,
} from "../../../../provisionCards.utils";
import { ProvisionSlice, ProvisionSource } from "../../../../provisionHistory";
import {
  addedProvisionLineKey,
  showsUncoveredNote,
  isProvisionLineIncluded,
  leaseLineKeys,
  linesSelectState,
  linesStartTicked,
  setLeasesIncluded,
  setProvisionLinesIncluded,
  WHOLE_LEASE_KEY,
} from "../../../../provisionLineInclusion.utils";
import {
  IProvisionLineRow,
  provisionCalculation,
  provisionLineRows,
  typeLineAmount,
} from "../../../../provisionLineRows.utils";
import {
  formatMonthCount,
  provisionMonthsRows,
} from "../../../../provisionMonthsHint.utils";
import {
  tenantPeriodRows,
  tenantPeriodNote,
} from "../../../../provisionTenantHint.utils";
import {
  earlierLeaseHints,
  fullySettledProvisions,
  ISettledSliceSource,
  sharedDays,
} from "../../../../settledProvisions.utils";
import { addedProvisionKindOf } from "../../../../settlementKinds.utils";
import {
  ContractRef,
  IExtraProvision,
  SettlementDraftState,
} from "../../../../types";
import { ExtraProvisionModal } from "./ExtraProvisionModal";
import { InfoTip } from "./InfoTip";
import { PROVISION_COLUMNS } from "./provisionColumns";
import {
  AddProvisionButton,
  CELL_PADDING,
  IProvisionTableLine,
  IProvisionTableTenant,
  ProvisionAmount,
  ProvisionLinesTable,
} from "./ProvisionLinesTable";
import {
  CollapsibleCard,
  fmt,
  fmtDate,
  Hint,
  parentNote,
  StepHeader,
  Warn,
} from "./shared";
import { StepContext } from "./types";

// Months are formatted for display only — the engine owns the maths
// (monthsBetween), so the wizard can never show a different number than the
// one the settlement bills.
const fmtMonths = formatMonthCount;

// One provision line of a lease, as the rental contract names it
// (2026-10-16): the days this settlement counts for its slice, its part of
// the slice's amount and where the slice's other months went. A slice
// without a provision line on the lease that still counts an amount is one
// row named after the slice (see provisionLineRows.utils).
interface ProvisionRow {
  // IProvisionLineRow.key: the line's key, or `slice:<slice>`.
  key: string;
  slice: ProvisionSlice;
  line?: IProvisionLine;
  intervals: Interval[];
  months: number;
  computed: number;
  expected: number;
  isTypedIn: boolean;
  // Earlier settlements that counted days of this slice in the period this
  // settlement covers ("al verrekend in …").
  settledIn: ISettledSliceSource[];
  // The slice's computed amount, and the rows of its lines: an amount typed
  // in for a line is summed over them into the slice's amount.
  sliceComputed: number;
  sliceRows: IProvisionLineRow[];
}

interface ProvisionLine {
  contract: ContractRef;
  overlapFrom: string;
  overlapTo: string;
  // The days this settlement counts, of any slice.
  intervals: Interval[];
  months: number;
  // Computed from the provision history, and what counts (the broker's own
  // amount when they typed one).
  computed: number;
  expected: number;
  source: ProvisionSource | "manual";
  // Billed by Rentio for days outside the lease: information only.
  billedOutsideContract: number;
  // The provisions the broker added by hand for this tenant.
  extras: IExtraProvision[];
  // One row per provision line of the slices the lease weighs; empty when
  // none counts an amount. Absent when the broker typed in one amount for
  // the whole lease (a draft made before the slices): that amount stays one
  // row.
  rows?: ProvisionRow[];
  // The keys of every line shown, the ones added by hand included (see
  // leaseLineKeys), and what of the lease's own provision counts with the
  // lines the broker left ticked: the engine's own figures.
  lineKeys: string[];
  included: IIncludedLeaseProvision;
  // The lease's lines an earlier settlement left out, open for days of
  // this period (decision 14): each counts on a row of its own.
  reopened: IReopenedLine[];
}

const SLICE_KEYS: Record<ProvisionSlice, string> = {
  common: "settlement.provisions.slice.common",
  private: "settlement.provisions.slice.private",
};

// A row's name as the rental contract gives it: the common and the
// non-common ("privatieve") costs by their contract names, an extra line by
// its own description; a slice without a line by the slice's name.
const provisionRowLabel = (row: Pick<ProvisionRow, "line" | "slice">) => {
  const { line } = row;
  if (!line) return getLocalizedText(SLICE_KEYS[row.slice]);
  if (line.kind === "common") return getLocalizedText(SLICE_KEYS.common);
  if (line.kind === "nonCommon") return getLocalizedText(SLICE_KEYS.private);
  return (
    line.description ??
    getLocalizedText("settlement.provisions.line.additional_fallback")
  );
};

// The ticked lines of a lease, and what they take off the tenant's balance:
// the lease's provision that counts and the ones added by hand.
const ticked = (params: {
  line: ProvisionLine;
  isOn: (contractId: string, lineKey: string) => boolean;
}): { count: number; amount: number } => {
  const { line, isOn } = params;
  const count = line.lineKeys.filter(key => isOn(line.contract.id, key)).length;
  const extras = line.extras
    .filter(extra => isOn(line.contract.id, addedProvisionLineKey(extra.id)))
    .reduce((sum, extra) => sum + extra.amount, 0);
  const reopened = line.reopened
    .filter(open => isOn(line.contract.id, open.key))
    .reduce((sum, open) => sum + open.amount, 0);
  return { count, amount: line.included.expected + reopened + extras };
};

// What a provision added by hand says under its description: its kind.
const ADDED_KIND_KEYS: Record<"common" | "private", string> = {
  common: "settlement.provisions.line.added_common",
  private: "settlement.provisions.line.added_private",
};

// A draft-local id for a provision added by hand.
const newExtraId = (): string =>
  `extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// The lease charges VAT on the provision this settlement weighs, so its
// amounts are shown as incl. VAT.
const provisionInclVat = (contract: ContractRef): boolean =>
  (contract.provisionSlices ?? []).some(
    slice => contract.provisionVatIncluded?.[slice],
  );

const fmtIntervals = (intervals: { from: string; to: string }[]): string =>
  intervals.map(iv => `${fmtDate(iv.from)} → ${fmtDate(iv.to)}`).join(", ");

// Two lists of days that hold the same days.
const sameDays = (left: Interval[], right: Interval[]): boolean =>
  fmtIntervals(left) === fmtIntervals(right);

// The "i" after a calculation: the months this settlement counts, the ones
// not due yet (D7), and the average when the amount changed in the period.
const MonthsInfo: React.FC<{
  intervals: Interval[];
  today: string;
  averagePerMonth?: number;
}> = ({ intervals, today, averagePerMonth }) => (
  <InfoTip
    rows={provisionMonthsRows({
      intervals,
      today,
      averagePerMonth:
        averagePerMonth != null ? fmt(averagePerMonth) : undefined,
    })}
  />
);

const CALC_KEYS = {
  exact: {
    one: "settlement.provisions.calc_one",
    many: "settlement.provisions.calc_many",
  },
  average: {
    one: "settlement.provisions.calc_average_one",
    many: "settlement.provisions.calc_average_many",
  },
};

// "€ 55,00 × 6 maanden", or "gem. € 52,30 × 6 maanden" when the months
// held other amounts (provisionCalculation), with the D7 "i".
const Calculation: React.FC<{
  computed: number;
  months: number;
  monthly?: number;
  intervals: Interval[];
  today: string;
  inclVat: boolean;
  isIncluded: boolean;
  // A source other than the lease itself ("Schatting", "Uit
  // betaaluitnodigingen"), after the calculation.
  source?: React.ReactNode;
}> = ({
  computed,
  months,
  monthly,
  intervals,
  today,
  inclVat,
  isIncluded,
  source,
}) => {
  const calc = provisionCalculation({ computed, months, monthly });
  // How the amount came about, all of it in the text's own font (Lewis,
  // 2026-10-17): only the "Bedrag" column is set in the financial one.
  const text = calc
    ? getLocalizedText(
        CALC_KEYS[calc.isAverage ? "average" : "exact"][
          calc.months === 1 ? "one" : "many"
        ],
        { months: fmtMonths(calc.months), amount: fmt(calc.perMonth) },
      )
    : "—";
  return (
    <div>
      <TextStyle variation={isIncluded ? "default" : "subdued"}>
        {text}
        {inclVat &&
          calc &&
          ` · ${getLocalizedText("settlement.provisions.incl_vat")}`}
      </TextStyle>
      <MonthsInfo
        intervals={intervals}
        today={today}
        averagePerMonth={calc?.isAverage ? calc.perMonth : undefined}
      />
      {source && <SourceTag>{source}</SourceTag>}
    </div>
  );
};

// The tenant above their lines: their name, the part of the settlement
// period they rented (only when they moved in or out within it) and its "i"
// on one line, the parents on the lease under it.
const TenantName = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 12px;
`;

const SourceTag = styled.span`
  margin-left: 8px;
`;

const IntroCopy = styled.div`
  margin-bottom: 24px;
`;

const BannerSpacing = styled.div`
  margin-bottom: 16px;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 24px;
`;

// The grand total under a building's cards, as far in from the card's edge
// as the amounts in the cells above it (a card's own total gets that from
// CollapsibleCard's TotalsDivider).
const GrandTotal = styled.div`
  padding-right: ${CELL_PADDING};
`;

// A narrow, right-aligned amount input.
const AmountInputWrap = styled.div`
  max-width: 130px;
  margin-left: auto;

  input {
    text-align: right;
  }
`;

// An amount's input while it is changed: the room before the line's
// actions, the input on its right.
const EditingWrap = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

// An amount typed the Belgian way like every amount of the feature ("1.250"
// is € 1.250, not € 1,25), passed on at every keystroke.
const AmountInput: React.FC<{
  name: string;
  initial: number;
  disabled: boolean;
  // null when the field is emptied or unreadable — lets a caller fall back
  // to a default.
  onChange: (amount: number | null) => void;
}> = ({ name, initial, disabled, onChange }) => (
  <AmountInputWrap>
    <LocaleNumberField
      name={name}
      isMoney
      maxDecimals={MONEY_DECIMALS}
      placeholder={formatMoney(0)}
      value={initial > 0 ? initial : undefined}
      disabled={disabled}
      onChange={value => onChange(value ?? null)}
    />
  </AmountInputWrap>
);

// The amount of a line: plain text with a pencil, which opens the input;
// leaving the field or Enter keeps what was typed, Escape drops it. An
// amount typed in carries "Aangepast", the computed one in its tooltip.
// People rarely change it, so the line does not read as a form to fill in.
const EditableAmount: React.FC<{
  name: string;
  amount: number;
  computed: number;
  isTypedIn: boolean;
  isIncluded: boolean;
  onCommit: (amount: number | null) => void;
}> = ({ name, amount, computed, isTypedIn, isIncluded, onCommit }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const typed = React.useRef<{ amount: number | null } | undefined>();
  const wrap = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isEditing) wrap.current?.querySelector("input")?.focus();
  }, [isEditing]);

  const close = (keep: boolean) => {
    const pending = typed.current;
    typed.current = undefined;
    setIsEditing(false);
    if (keep && pending) onCommit(pending.amount);
  };

  if (isEditing) {
    return (
      <ProvisionAmount
        amount={
          <EditingWrap
            ref={wrap}
            onBlur={event => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                close(true);
              }
            }}
            onKeyDown={event => {
              if (event.key === "Enter") {
                event.preventDefault();
                close(true);
              }
              if (event.key === "Escape") close(false);
            }}
          >
            <AmountInput
              name={name}
              initial={amount}
              disabled={false}
              onChange={next => {
                typed.current = { amount: next };
              }}
            />
          </EditingWrap>
        }
      />
    );
  }

  const editLabel = getLocalizedText("settlement.provisions.edit_amount");
  return (
    <ProvisionAmount
      before={
        isTypedIn && (
          <Tooltip
            tooltipContent={getLocalizedText("settlement.provisions.computed", {
              amount: fmt(computed),
            })}
          >
            <TextStyle variation="subdued" size="small">
              {getLocalizedText("settlement.provisions.source.manual")}
            </TextStyle>
          </Tooltip>
        )
      }
      amount={
        <Money amount={amount} variation={isIncluded ? undefined : "subdued"} />
      }
      edit={
        isIncluded && (
          <Button
            appearance="plain"
            onClick={() => setIsEditing(true)}
            ariaLabel={editLabel}
            title={editLabel}
          >
            <Icon source="edit" size="small" />
          </Button>
        )
      }
    />
  );
};

// Totals slot under the card — mirrors the cost step's SelectedTotal, in the
// font of the other amounts. It counts provision lines, each with its own
// checkbox, not tenants: "4 van 5 provisies meegenomen".
const ProvisionsTotal: React.FC<{
  lines: ProvisionLine[];
  isOn: (contractId: string, lineKey: string) => boolean;
}> = ({ lines, isOn }) => {
  const parts = lines.map(line => ticked({ line, isOn }));
  const count = parts.reduce((sum, part) => sum + part.count, 0);
  const lineCount = lines.reduce((sum, line) => sum + line.lineKeys.length, 0);
  const total = +parts.reduce((sum, part) => sum + part.amount, 0).toFixed(2);
  return (
    <TotalRow>
      {/* The count on a small line under the label (Lewis, 2026-10-17),
          as the cost choice's total says how many costs it holds. */}
      <div>
        <TextStyle variation="strong">
          {getLocalizedText("settlement.provisions.total_expected")}
        </TextStyle>
        <div>
          <TextStyle variation="subdued" size="small">
            {getLocalizedText(
              lineCount === 1
                ? "settlement.provisions.lines_included_one"
                : "settlement.provisions.lines_included_many",
              {
                included: String(count),
                total: String(lineCount),
              },
            )}
          </TextStyle>
        </div>
      </div>
      <ProvisionAmount amount={<Money amount={total} variation="strong" />} />
    </TotalRow>
  );
};

export const Step3Provisions: React.FC<
  StepContext & { result: ComputeResult }
> = ({
  draft,
  settlement,
  contracts,
  assignable,
  saveDraft,
  settledProvisionIntervals,
  provisionsFromEarlierLeases,
  provisionsSettledBy,
  openProvisionLines,
  result,
}) => {
  // Which lines a chosen cost of their kind reaches, as the engine reads it
  // (provisionLineInclusion.utils): a ticked line none reaches is warned.
  const coverage = result.provisionCoverage;
  // No chosen cost charges a tenant anything (owner costs only): the lines
  // start unticked, said once at the top (Lewis, 2026-10-19).
  const startsTicked = linesStartTicked(coverage);
  const expectedOverride = draft.provisionExpectedOverride ?? {};
  const sliceOverride = draft.provisionExpectedOverrideBySlice ?? {};
  const lineOverride = draft.provisionExpectedOverrideByLine ?? {};
  // The amount the broker typed in for one slice of a lease; the computed
  // one again clears it. A slice's own amount replaces the one typed in for
  // the whole lease (a draft made before the slices), which then goes.
  const slicePatch = (params: {
    contractId: string;
    slice: ProvisionSlice;
    amount: number | null;
    computed: number;
  }) => {
    const { contractId, slice, amount, computed } = params;
    const ofLease = { ...sliceOverride[contractId] };
    if (amount == null || Math.abs(amount - computed) < 0.005) {
      delete ofLease[slice];
    } else {
      ofLease[slice] = amount;
    }
    const next = { ...sliceOverride, [contractId]: ofLease };
    if (Object.keys(ofLease).length === 0) delete next[contractId];
    const flat = { ...expectedOverride };
    delete flat[contractId];
    return {
      provisionExpectedOverrideBySlice: next,
      provisionExpectedOverride: flat,
    };
  };
  // The amount typed in for one row: a line's goes into its slice's amount
  // (the sum of the slice's lines) and is remembered per line; a row without
  // a line is its slice.
  const setRowAmount = (params: {
    contractId: string;
    row: ProvisionRow;
    amount: number | null;
  }) => {
    const { contractId, row, amount } = params;
    if (!row.line) {
      saveDraft(
        slicePatch({
          contractId,
          slice: row.slice,
          amount,
          computed: row.computed,
        }),
      );
      return;
    }
    const { typedByLine, sliceAmount } = typeLineAmount({
      rows: row.sliceRows,
      lineKey: row.key,
      amount,
      typedByLine: lineOverride[contractId],
    });
    const nextLines = { ...lineOverride, [contractId]: typedByLine };
    if (Object.keys(typedByLine).length === 0) delete nextLines[contractId];
    saveDraft({
      ...slicePatch({
        contractId,
        slice: row.slice,
        amount: sliceAmount,
        computed: row.sliceComputed,
      }),
      provisionExpectedOverrideByLine: nextLines,
    });
  };
  // Whether one line of a lease counts (provisionLineInclusion.utils).
  const isOn = (contractId: string, lineKey: string): boolean =>
    isProvisionLineIncluded({ draft, contractId, lineKey, startsTicked });
  const extrasByContract = draft.extraProvisions ?? {};
  const saveExtras = (
    contractId: string,
    extras: IExtraProvision[],
    patch?: Partial<SettlementDraftState>,
  ) => {
    const next = { ...extrasByContract, [contractId]: extras };
    if (extras.length === 0) delete next[contractId];
    saveDraft({ ...patch, extraProvisions: next });
  };
  // The provision added by hand whose modal is open: of which lease, and
  // which one when it is changed rather than added.
  const [extraForm, setExtraForm] = React.useState<
    { contractId: string; extraId?: string } | undefined
  >();
  // A provision added by hand starts ticked, also on a lease whose other
  // lines are all unticked.
  const addExtra = (
    l: ProvisionLine,
    values: Pick<IExtraProvision, "title" | "amount">,
  ) => {
    const extra = { id: newExtraId(), ...values };
    const key = addedProvisionLineKey(extra.id);
    saveExtras(l.contract.id, [...l.extras, extra], {
      ...setProvisionLinesIncluded({
        draft,
        contractId: l.contract.id,
        lineKeys: [...l.lineKeys, key],
        changes: { [key]: true },
      }),
    });
  };
  const updateExtra = (
    contractId: string,
    extraId: string,
    patch: Partial<IExtraProvision>,
  ) =>
    saveExtras(
      contractId,
      (extrasByContract[contractId] ?? []).map(extra =>
        extra.id === extraId ? { ...extra, ...patch } : extra,
      ),
    );
  // Removing one also drops its checkbox: the lease's other lines say
  // whether it still counts at all.
  const removeExtra = (l: ProvisionLine, extraId: string) =>
    saveExtras(
      l.contract.id,
      l.extras.filter(extra => extra.id !== extraId),
      setProvisionLinesIncluded({
        draft,
        contractId: l.contract.id,
        lineKeys: l.lineKeys.filter(
          key => key !== addedProvisionLineKey(extraId),
        ),
        changes: {},
      }),
    );

  const propertyOf = (id?: string) =>
    assignable.find(p => String(p.id) === String(id));

  // Every month of the settlement period in which a lease ran counts,
  // whichever costs were chosen (D7). Per slice of the provision: each slice
  // skips the months an earlier settlement already counted for it, as the
  // engine does.
  const intervalsBySlice = provisionIntervalsBySlice(
    contracts,
    draft.period,
    settledProvisionIntervals,
  );

  // Each lease's days in the period before an earlier settlement took any
  // off: what "al verrekend in …" compares with, and what tells an empty
  // table whose months were all settled from one without leases.
  const coveredBeforeSettled = provisionIntervalsByContract(
    contracts,
    draft.period,
  );
  const today = todayIsoDay();

  // The days of one slice of a lease that an earlier settlement already
  // counted in this settlement's period, by that settlement's name.
  const settledInOf = (
    c: ContractRef,
    slice: ProvisionSlice,
  ): ISettledSliceSource[] =>
    (provisionsSettledBy?.[c.id]?.[slice] ?? [])
      .map(source => ({
        settlementName: source.settlementName,
        intervals: sharedDays(
          coveredBeforeSettled[c.id] ?? [],
          source.intervals,
        ),
      }))
      .filter(source => source.intervals.length > 0);

  const provisionRowsOf = (
    c: ContractRef,
    lease: ReturnType<typeof leaseExpectedProvision>,
  ): ProvisionRow[] | undefined => {
    const typedBySlice = sliceOverride[c.id] ?? {};
    const hasTypedSlice = Object.values(typedBySlice).some(v => v != null);
    // One amount typed in for the whole lease stays one row.
    if (!hasTypedSlice && expectedOverride[c.id] != null) return undefined;
    const slices = contractProvisionSlices(c).map(slice => {
      const intervals = intervalsBySlice[c.id]?.[slice] ?? [];
      const computed = lease.calc.bySlice[slice]?.expected ?? 0;
      const typed = typedBySlice[slice];
      return {
        slice,
        intervals,
        months:
          lease.calc.bySlice[slice]?.months ??
          intervals.reduce((sum, iv) => sum + monthsBetween(iv.from, iv.to), 0),
        computed,
        expected: typed ?? lease.bySlice?.[slice] ?? computed,
        isTypedIn: typed != null,
        settledIn: settledInOf(c, slice),
      };
    });
    // A slice without a provision says nothing: shown only when it counts
    // an amount, was typed in, or its months went elsewhere. Its lines
    // (forfait lines are none) each get a row.
    return slices
      .filter(
        slice =>
          Math.abs(slice.expected) >= 0.005 ||
          slice.isTypedIn ||
          slice.settledIn.length > 0,
      )
      .flatMap(slice => {
        const sliceRows = provisionLineRows({
          slice: slice.slice,
          lines: c.provisionLines ?? [],
          computed: slice.computed,
          expected: slice.expected,
          sliceTypedIn: slice.isTypedIn,
          typedByLine: lineOverride[c.id],
        });
        return sliceRows.map(
          (row): ProvisionRow => ({
            key: row.key,
            slice: slice.slice,
            line: row.line,
            intervals: slice.intervals,
            months: slice.months,
            computed: row.computed,
            expected: row.expected,
            isTypedIn: row.isTypedIn,
            settledIn: slice.settledIn,
            sliceComputed: slice.computed,
            sliceRows,
          }),
        );
      });
  };

  // The leases that take part in the settlement, as the summary shows them.
  const partyIds = new Set(result.perContract.map(pc => pc.contractId));
  const lines: ProvisionLine[] = contracts
    .map((c): ProvisionLine | null => {
      const intervals = unionOfSlices(intervalsBySlice[c.id]);
      const reopened = reopenedProvisionLines({
        contract: c,
        openLines: openProvisionLines?.[c.id],
        period: draft.period,
      }).filter(open => Math.abs(open.amount) >= 0.005);
      const shownDays = unionIntervals([
        intervals,
        ...reopened.map(open => open.intervals),
      ]);
      if (shownDays.length === 0) return null;
      const overlapFrom = shownDays[0].from;
      const overlapTo = shownDays[shownDays.length - 1].to;
      const lease = leaseExpectedProvision({
        contract: c,
        intervalsBySlice: intervalsBySlice[c.id] ?? {},
        override: expectedOverride[c.id],
        overrideBySlice: sliceOverride[c.id],
      });
      const { calc } = lease;
      const rows = provisionRowsOf(c, lease);
      const extras = extrasByContract[c.id] ?? [];
      return {
        rows,
        reopened,
        lineKeys: [
          ...leaseLineKeys({
            rowKeys: rows?.map(row => row.key),
            extraIds: extras.map(extra => extra.id),
          }),
          ...reopened.map(open => open.key),
        ],
        included: includedLeaseProvision({
          contract: c,
          lease,
          draft,
          startsTicked,
        }),
        contract: c,
        overlapFrom,
        overlapTo,
        intervals,
        months: calc.months,
        computed: calc.expected,
        expected: lease.expected,
        source: lease.isTypedIn ? "manual" : calc.source,
        billedOutsideContract: calc.billedOutsideContract,
        extras,
      };
    })
    .filter((x): x is ProvisionLine => x !== null)
    // A lease that takes no part in the settlement (nothing to settle, M18)
    // and has no provision to show here: no row of "— · Al verrekend" (M21).
    .filter(
      l =>
        partyIds.has(l.contract.id) ||
        l.extras.length > 0 ||
        l.reopened.length > 0 ||
        Math.abs(l.expected) >= 0.005 ||
        (l.rows ?? []).some(row => Math.abs(row.expected) >= 0.005),
    );
  // The tenants who take part with their costs while their provision days
  // were all settled before (M21): named under the step, with where.
  const fullySettled = fullySettledProvisions({
    hidden: contracts.filter(c => !lines.some(l => l.contract.id === c.id)),
    partyIds,
    sourcesOf: c => [...settledInOf(c, "common"), ...settledInOf(c, "private")],
  });

  // One card per unit, headed by its full address as the cost choice's cards
  // are ("Scenariolaan 1 bus 3, 9000 Gent (2)"), in the order of their box
  // numbers; the leases of a card in the order they started. A unit or a
  // departure settlement is one card, headed by its unit's address. A lease
  // whose unit the wizard does not know goes into a last card.
  const unitProperty =
    settlement.scope === "unit" ? propertyOf(settlement.unitId) : undefined;
  const cards: IProvisionCard<ProvisionLine>[] =
    settlement.scope === "unit"
      ? [
          {
            key: "unit",
            label: unitProperty
              ? unitAddressLabel(unitProperty)
              : getLocalizedText("settlement.provisions.card_title"),
            lines: [...lines].sort(byLeaseStart),
          },
        ]
      : provisionCards({
          lines,
          unitLabelOf: unitId => {
            const unit = propertyOf(unitId);
            return unit ? unitAddressLabel(unit) : undefined;
          },
          unitSortLabelOf: unitId => {
            const unit = propertyOf(unitId);
            return unit ? unitBoxLabel(unit) : undefined;
          },
          unknownUnitTitle: getLocalizedText(
            "settlement.provisions.unknown_unit_card",
          ),
        });
  // The leases in the order of the cards, for the banners above them.
  const orderedLines = cards.flatMap(card => card.lines);

  // One line ticked or unticked.
  const setLineIncluded = (l: ProvisionLine, lineKey: string, next: boolean) =>
    saveDraft(
      setProvisionLinesIncluded({
        draft,
        contractId: l.contract.id,
        lineKeys: l.lineKeys,
        changes: { [lineKey]: next },
      }),
    );
  // A tenant's own checkbox, and a card's select-all: every line of those
  // leases.
  const setManyIncluded = (manyLines: ProvisionLine[], next: boolean) =>
    saveDraft(
      setLeasesIncluded({
        draft,
        leases: manyLines.map(l => ({
          contractId: l.contract.id,
          lineKeys: l.lineKeys,
        })),
        next,
      }),
    );
  // Contracts settled on today's amount because Rentio found no requests or
  // indexations for (part of) the period — the broker should check those.
  const estimatedTenants = Array.from(
    new Set(
      orderedLines
        .filter(l => l.source === "estimate" && l.included.slices.length > 0)
        .map(l => l.contract.tenantName),
    ),
  );

  const isEmpty = lines.length === 0;
  const hasContracts = contracts.length > 0;
  // Months that overlap but were all settled by an earlier settlement: say so,
  // instead of suggesting there are no contracts or no costs. (A settlement
  // without costs is allowed — it refunds provisions over its own period.)
  const allAlreadySettled =
    isEmpty &&
    Object.values(coveredBeforeSettled).some(intervals => intervals.length > 0);
  // Months an earlier lease of the same tenant in the same unit already
  // settled (a lease ended early and re-created, or entered again): they drop
  // out here, and the broker should see why.
  const earlierLeaseMonths = earlierLeaseHints({
    contracts,
    intervals: coveredBeforeSettled,
    fromEarlierLeases: provisionsFromEarlierLeases ?? {},
  });
  // The lease whose provision added by hand is being added or changed, and
  // that provision when it is changed.
  const extraFormLine = extraForm
    ? lines.find(l => l.contract.id === extraForm.contractId)
    : undefined;
  const editedExtra = extraFormLine?.extras.find(
    extra => extra.id === extraForm?.extraId,
  );
  const emptyHeading = !hasContracts
    ? getLocalizedText("settlement.provisions.empty.no_contracts")
    : allAlreadySettled
      ? getLocalizedText("settlement.provisions.empty.already_settled")
      : getLocalizedText("settlement.provisions.empty.no_overlap");

  // A tenant and their provision lines (Lewis, 2026-10-16): the tenant on a
  // light row of their own with the subtotal of their ticked lines, each
  // provision line on one compact row with its own checkbox, the provisions
  // added by hand after them, and the button to add one as the last row.
  const tenantOf = (l: ProvisionLine): IProvisionTableTenant => {
    const contractId = l.contract.id;
    const inclVat = provisionInclVat(l.contract);
    const lineSelect = (lineKey: string) => ({
      checked: isOn(contractId, lineKey),
      onChange: (next: boolean) => setLineIncluded(l, lineKey, next),
    });
    // A source other than the lease itself, after the calculation:
    // "Schatting" in the warning color, "Uit betaaluitnodigingen", …
    const sourceTag = (isIncluded: boolean) =>
      l.source === "contract" || l.source === "manual" ? undefined : (
        <TextStyle
          variation={isIncluded && l.source === "estimate" ? "warn" : "subdued"}
          size="small"
        >
          {getLocalizedText(`settlement.provisions.source.${l.source}`)}
        </TextStyle>
      );
    const small = (text: React.ReactNode) => (
      <div>
        <TextStyle variation="subdued" size="small">
          {text}
        </TextStyle>
      </div>
    );
    // Under an unticked line: a later settlement can still count its
    // months. A line is kept open per line (decision 14), also when its
    // slice still counts here with the lines left ticked.
    const excludedNote = () =>
      small(getLocalizedText("settlement.provisions.line.excluded_later"));
    // A provision added by hand: a row like the lease's own lines, its
    // description as its label, "Zelf toegevoegd" where the others say how
    // they were calculated (Lewis, 2026-10-17: no tag by the description),
    // its amount with a pencil (the same modal changes the description and
    // the amount) and a bin. One kept without a description or with 0 (a
    // draft made before the modal) still shows, so nothing gets lost.
    const editExtraLabel = getLocalizedText("settlement.provisions.edit_extra");
    const removeExtraLabel = getLocalizedText(
      "settlement.provisions.remove_extra",
    );
    const extraLines: IProvisionTableLine[] = l.extras.map(extra => {
      const lineKey = addedProvisionLineKey(extra.id);
      const included = isOn(contractId, lineKey);
      const title = extra.title.trim();
      // The kind the broker chose for it (decision 20), as the lease's
      // extra lines say theirs.
      const kindNote = getLocalizedText(
        ADDED_KIND_KEYS[addedProvisionKindOf(extra)],
      );
      return {
        key: lineKey,
        select: lineSelect(lineKey),
        label: (
          <div>
            <TextStyle variation={included && title ? "default" : "subdued"}>
              {title ||
                getLocalizedText("settlement.provisions.extra_untitled")}
            </TextStyle>
            {small(kindNote)}
          </div>
        ),
        calculation: (
          <TextStyle variation="subdued">
            {getLocalizedText("settlement.provisions.source.extra")}
          </TextStyle>
        ),
        amount: (
          <ProvisionAmount
            amount={
              <Money
                amount={extra.amount}
                variation={included ? undefined : "subdued"}
              />
            }
            edit={
              included && (
                <Button
                  appearance="plain"
                  onClick={() =>
                    setExtraForm({ contractId, extraId: extra.id })
                  }
                  ariaLabel={editExtraLabel}
                  title={editExtraLabel}
                >
                  <Icon source="edit" size="small" />
                </Button>
              )
            }
            remove={
              <Button
                appearance="plain"
                onClick={() => removeExtra(l, extra.id)}
                ariaLabel={removeExtraLabel}
                title={removeExtraLabel}
              >
                <Icon source="bin" size="small" />
              </Button>
            }
          />
        ),
      };
    });
    // The tenant: their name, the part of the settlement period they rented
    // when they moved in or out within it, and an "i" that says it all in
    // full sentences; the parents on the lease and what Rentio billed
    // outside it on a small line under it. The unit is the card's title, not
    // repeated here.
    const parents = parentNote(l.contract.parentNames);
    const periodNote = tenantPeriodNote({
      lease: l.contract,
      period: draft.period,
      today,
    });
    const slices = contractProvisionSlices(l.contract);
    const periodDetails = tenantPeriodRows({
      lease: l.contract,
      slices,
      countedBySlice: intervalsBySlice[contractId] ?? {},
      counted: l.intervals,
      settledBySlice: Object.fromEntries(
        slices.map(slice => [slice, settledInOf(l.contract, slice)]),
      ),
      earlierLease: earlierLeaseMonths.find(
        earlier => earlier.contract.id === contractId,
      ),
      today,
    });
    const header = (
      <div>
        <TenantName>
          <TextStyle variation="strong">{l.contract.tenantName}</TextStyle>
          <span>
            {periodNote && (
              <TextStyle variation="subdued" size="small">
                {periodNote}
              </TextStyle>
            )}
            <InfoTip rows={periodDetails} />
          </span>
        </TenantName>
        {parents && small(parents)}
        {l.billedOutsideContract > 0 &&
          small(
            getLocalizedText("settlement.provisions.billed_outside_contract", {
              amount: fmt(l.billedOutsideContract),
            }),
          )}
      </div>
    );
    // A ticked line no chosen cost of its kind reaches (Lewis, 2026-10-17):
    // it stays ticked, as a settlement normally settles everything, and a
    // calm line under it says that its provision goes back in full. The
    // broker can still untick it.
    // Only on a line that counts a provision (M27): a line of € 0 has
    // nothing to give back.
    const uncoveredNote = (
      lineKey: string,
      included: boolean,
      amount: number,
    ) =>
      showsUncoveredNote({
        contractId,
        lineKey,
        coverage,
        included,
        amount,
      }) && (
        <div>
          <TextStyle variation="warn" size="small">
            {getLocalizedText("settlement.provisions.uncovered.warning")}
          </TextStyle>
        </div>
      );
    // The lines an earlier settlement left out, counted here for their open
    // days (decision 14): named as on the lease, with where they were left
    // out, and their part of their slice for those days.
    const reopenedLines: IProvisionTableLine[] = l.reopened.map(open => {
      const included = isOn(contractId, open.key);
      return {
        key: open.key,
        select: lineSelect(open.key),
        label: (
          <div>
            <TextStyle variation={included ? "default" : "subdued"}>
              {provisionRowLabel({ line: open.line, slice: open.slice })}
            </TextStyle>
            {small(
              getLocalizedText("settlement.provisions.line.reopened", {
                settlements: open.settlementNames.join(", "),
                periods: fmtIntervals(open.intervals),
              }),
            )}
            {!included && excludedNote()}
          </div>
        ),
        calculation: (
          <Calculation
            computed={open.amount}
            months={open.months}
            monthly={open.line.monthly}
            intervals={open.intervals}
            today={today}
            inclVat={inclVat}
            isIncluded={included}
          />
        ),
        amount: (
          <ProvisionAmount
            amount={
              <Money
                amount={open.amount}
                variation={included ? undefined : "subdued"}
              />
            }
          />
        ),
      };
    });
    const tenant = (lines: IProvisionTableLine[]): IProvisionTableTenant => ({
      key: contractId,
      header,
      subtotal: (
        <Money amount={ticked({ line: l, isOn }).amount} variation="strong" />
      ),
      ...(l.lineKeys.length > 0
        ? {
            select: {
              state: linesSelectState(
                l.lineKeys.map(lineKey => isOn(contractId, lineKey)),
              ),
              onChange: (next: boolean) => setManyIncluded([l], next),
            },
          }
        : {}),
      lines: [...lines, ...reopenedLines, ...extraLines],
      // A quiet text button where the lines' text starts (Lewis chose it,
      // 2026-10-16): see AddProvisionButton.
      footer: (
        <AddProvisionButton
          label={getLocalizedText("settlement.provisions.add_extra")}
          onClick={() => setExtraForm({ contractId })}
        />
      ),
    });

    // One row per provision line of the lease (2026-10-16): its name, how
    // its amount was calculated and its amount. Where its days differ from
    // the tenant's (months already settled), a small line says which.
    if (l.rows && l.rows.length > 0) {
      const rowLine = (row: ProvisionRow): IProvisionTableLine => {
        const included = isOn(contractId, row.key);
        const daysNote = [
          ...(sameDays(row.intervals, l.intervals)
            ? []
            : [row.intervals.length > 0 ? fmtIntervals(row.intervals) : "—"]),
          ...row.settledIn.map(source =>
            getLocalizedText("settlement.provisions.slice_settled_in", {
              settlement: source.settlementName,
              periods: fmtIntervals(source.intervals),
            }),
          ),
        ];
        return {
          key: row.key,
          select: lineSelect(row.key),
          label: (
            <div>
              <TextStyle variation={included ? "default" : "subdued"}>
                {provisionRowLabel(row)}
              </TextStyle>
              {/* An extra line of the lease counts in the private costs'
                    provision (Lewis, 2026-10-16): say so. */}
              {row.line?.kind === "additional" &&
                small(
                  getLocalizedText(
                    "settlement.provisions.line.additional_private",
                  ),
                )}
              {daysNote.length > 0 && small(daysNote.join(" · "))}
              {uncoveredNote(row.key, included, row.expected)}
              {!included && excludedNote()}
            </div>
          ),
          calculation: (
            <Calculation
              computed={row.computed}
              months={row.months}
              monthly={row.line?.monthly}
              intervals={row.intervals}
              today={today}
              inclVat={inclVat}
              isIncluded={included}
              source={sourceTag(included)}
            />
          ),
          amount: (
            <EditableAmount
              key={`expected-${contractId}-${row.key}`}
              name={`expected-${contractId}-${row.key}`}
              amount={row.expected}
              computed={row.computed}
              isTypedIn={row.isTypedIn}
              isIncluded={included}
              onCommit={amount => setRowAmount({ contractId, row, amount })}
            />
          ),
        };
      };
      return tenant(l.rows.map(rowLine));
    }

    // No provision line counts in this settlement (forfait costs only, or
    // none at all): one row says so, without a checkbox. The provisions
    // added by hand still come under it.
    if (l.rows) {
      return tenant([
        {
          key: "none",
          label: (
            <TextStyle variation="subdued">
              {getLocalizedText("settlement.provisions.line.none")}
            </TextStyle>
          ),
          calculation: (
            <div>
              <TextStyle variation="subdued">—</TextStyle>
              <MonthsInfo intervals={l.intervals} today={today} />
            </div>
          ),
          amount: (
            <ProvisionAmount
              amount={<Money amount={0} variation="subdued" />}
            />
          ),
        },
      ]);
    }

    // One amount typed in for the whole lease (a draft made before the
    // slices): one row over the slices the lease weighs.
    const included = isOn(contractId, WHOLE_LEASE_KEY);
    const wholeLeaseLine: IProvisionTableLine = {
      key: WHOLE_LEASE_KEY,
      select: lineSelect(WHOLE_LEASE_KEY),
      label: (
        <div>
          <TextStyle variation={included ? "default" : "subdued"}>
            {contractProvisionSlices(l.contract)
              .map(slice => getLocalizedText(SLICE_KEYS[slice]))
              .join(" + ")}
          </TextStyle>
          {uncoveredNote(WHOLE_LEASE_KEY, included, l.expected)}
          {!included && excludedNote()}
        </div>
      ),
      calculation: (
        <Calculation
          computed={l.computed}
          months={l.months}
          monthly={l.contract.monthlyProvision}
          intervals={l.intervals}
          today={today}
          inclVat={inclVat}
          isIncluded={included}
          source={sourceTag(included)}
        />
      ),
      // Typing the computed amount again clears the override.
      amount: (
        <EditableAmount
          key={`expected-${contractId}`}
          name={`expected-${contractId}`}
          amount={l.expected}
          computed={l.computed}
          isTypedIn={l.source === "manual"}
          isIncluded={included}
          onCommit={amount => {
            const next = { ...expectedOverride };
            if (amount == null || Math.abs(amount - l.computed) < 0.005) {
              delete next[contractId];
            } else {
              next[contractId] = amount;
            }
            saveDraft({ provisionExpectedOverride: next });
          }}
        />
      ),
    };
    return tenant([wholeLeaseLine]);
  };

  return (
    <div>
      <StepHeader>{getLocalizedText("settlement.provisions.title")}</StepHeader>
      <IntroCopy>
        <TextStyle variation="subdued">
          {getLocalizedText("settlement.provisions.intro_expected")}
        </TextStyle>
      </IntroCopy>

      {!startsTicked && !isEmpty && (
        <BannerSpacing>
          <Hint>
            {getLocalizedText("settlement.provisions.no_tenant_costs")}
          </Hint>
        </BannerSpacing>
      )}

      {estimatedTenants.length > 0 && (
        <BannerSpacing>
          <Warn>
            {getLocalizedText("settlement.provisions.estimate_warning", {
              tenants: estimatedTenants.join(", "),
            })}
          </Warn>
        </BannerSpacing>
      )}

      {earlierLeaseMonths.length > 0 && (
        <BannerSpacing>
          <Hint>
            {earlierLeaseMonths.map(earlier => (
              <div key={earlier.contract.id}>
                {getLocalizedText(
                  "settlement.provisions.settled_under_earlier_lease",
                  {
                    tenant: earlier.contract.tenantName,
                    periods: fmtIntervals(earlier.intervals),
                    settlements: earlier.settlementNames.join(", "),
                  },
                )}
              </div>
            ))}
          </Hint>
        </BannerSpacing>
      )}

      {fullySettled.length > 0 && (
        <BannerSpacing>
          <Hint>
            {fullySettled.map(settled => (
              <div key={settled.contract.id}>
                {getLocalizedText(
                  "settlement.provisions.fully_settled_before",
                  {
                    tenant: settled.contract.tenantName,
                    periods: fmtIntervals(settled.intervals),
                    settlements: settled.settlementNames.join(", "),
                  },
                )}
              </div>
            ))}
          </Hint>
        </BannerSpacing>
      )}

      {isEmpty ? (
        <EmptyState heading={emptyHeading} isShownInline />
      ) : (
        <Stack vertical spacing="loose">
          {cards.map(card => (
            <CollapsibleCard
              key={card.key}
              title={getLocalizedText("settlement.invoices.section_card", {
                label: card.label,
                count: String(card.lines.length),
              })}
              totals={<ProvisionsTotal lines={card.lines} isOn={isOn} />}
            >
              <ProvisionLinesTable
                headings={PROVISION_COLUMNS.map(column =>
                  getLocalizedText(column.heading),
                )}
                select={{
                  state: linesSelectState(
                    card.lines.flatMap(l =>
                      l.lineKeys.map(lineKey => isOn(l.contract.id, lineKey)),
                    ),
                  ),
                  onChange: next => setManyIncluded(card.lines, next),
                }}
                tenants={card.lines.map(tenantOf)}
              />
            </CollapsibleCard>
          ))}

          {/* A building's cards: the grand total of every card under them,
              as the cost choice does. */}
          {cards.length > 1 && (
            <Card>
              <GrandTotal>
                <ProvisionsTotal lines={orderedLines} isOn={isOn} />
              </GrandTotal>
            </Card>
          )}
        </Stack>
      )}

      {settlement.scope === "unit" && !hasContracts && (
        <div style={{ marginTop: 12 }}>
          <Warn>
            {getLocalizedText("settlement.provisions.no_contracts_warning")}
          </Warn>
        </div>
      )}

      {extraFormLine && (!extraForm?.extraId || editedExtra) && (
        <ExtraProvisionModal
          tenantName={extraFormLine.contract.tenantName}
          kindOptions={extraProvisionKindOptions({
            slices: contractProvisionSlices(extraFormLine.contract),
            fixedFeeKinds: leaseFixedFeeKinds({
              contract: extraFormLine.contract,
            }),
            tenantName: extraFormLine.contract.tenantName,
          })}
          initial={editedExtra}
          onClose={() => setExtraForm(undefined)}
          onSubmit={values => {
            if (editedExtra) {
              updateExtra(extraFormLine.contract.id, editedExtra.id, values);
            } else {
              addExtra(extraFormLine, values);
            }
            setExtraForm(undefined);
          }}
        />
      )}
    </div>
  );
};
